import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { NavLink } from "@/components/NavLink";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Phone, MessageCircle, Search, X, ChevronDown, ChevronRight, Mail, MapPin } from "lucide-react";
/**
 * OFFICIAL FEEL FREE HOLIDAYS LOGO
 * Do not change logo without owner approval.
 */
import officialLogo from "@/assets/logo-ffh-full.png";

interface SubItem {
  label: string;
  path: string;
}

interface DropdownItem {
  label: string;
  path: string;
  subItems?: SubItem[];
}

interface NavItem {
  to: string;
  label: string;
  dropdown?: DropdownItem[];
}

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [activeSubMenu, setActiveSubMenu] = useState<string | null>(null);
  const [mobileExpandedMenu, setMobileExpandedMenu] = useState<string | null>(null);
  const [mobileExpandedSubMenu, setMobileExpandedSubMenu] = useState<string | null>(null);
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const submenuTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 10);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const servicesDropdown: DropdownItem[] = [
    { label: "Customized Holidays", path: "/services" },
    { label: "Honeymoon Packages", path: "/services" },
    { label: "Family & Group Tours", path: "/services" },
    { label: "Visa Assistance", path: "/visa" },
    { label: "Cruise Holidays", path: "/services" },
  ];

  const destinationsDropdown: DropdownItem[] = [
    { 
      label: "India", 
      path: "/destinations",
      subItems: [
        { label: "Kashmir", path: "/destinations/kashmir" },
        { label: "Goa", path: "/destinations/goa" },
        { label: "Kerala", path: "/destinations/kerala" },
        { label: "Rajasthan", path: "/destinations/rajasthan" },
        { label: "Himachal Pradesh", path: "/destinations/himachal" },
      ]
    },
    { 
      label: "Asia", 
      path: "/destinations",
      subItems: [
        { label: "Bali", path: "/destinations/bali" },
        { label: "Singapore", path: "/destinations/singapore" },
        { label: "Thailand", path: "/destinations/thailand" },
        { label: "Vietnam", path: "/destinations/vietnam" },
        { label: "Malaysia", path: "/destinations/malaysia" },
        { label: "Maldives", path: "/destinations/maldives" },
      ]
    },
    { 
      label: "Middle East", 
      path: "/destinations",
      subItems: [
        { label: "Dubai", path: "/destinations/dubai" },
        { label: "Abu Dhabi", path: "/destinations/abu-dhabi" },
        { label: "Turkey", path: "/destinations/turkey" },
        { label: "Qatar", path: "/destinations/qatar" },
      ]
    },
    { 
      label: "Europe - Central & West", 
      path: "/destinations",
      subItems: [
        { label: "France", path: "/destinations/france" },
        { label: "Switzerland", path: "/destinations/switzerland" },
        { label: "Italy", path: "/destinations/italy" },
        { label: "Germany", path: "/destinations/germany" },
        { label: "Netherlands", path: "/destinations/netherlands" },
        { label: "Spain", path: "/destinations/spain" },
      ]
    },
    { 
      label: "Scandinavia", 
      path: "/destinations",
      subItems: [
        { label: "Norway", path: "/destinations/norway" },
        { label: "Sweden", path: "/destinations/sweden" },
        { label: "Denmark", path: "/destinations/denmark" },
        { label: "Finland", path: "/destinations/finland" },
        { label: "Iceland", path: "/destinations/iceland" },
      ]
    },
    { 
      label: "East Europe", 
      path: "/destinations",
      subItems: [
        { label: "Czech Republic", path: "/destinations/czech-republic" },
        { label: "Poland", path: "/destinations/poland" },
        { label: "Hungary", path: "/destinations/hungary" },
        { label: "Austria", path: "/destinations/austria" },
      ]
    },
    { 
      label: "Baltic Countries", 
      path: "/destinations",
      subItems: [
        { label: "Estonia", path: "/destinations/estonia" },
        { label: "Latvia", path: "/destinations/latvia" },
        { label: "Lithuania", path: "/destinations/lithuania" },
      ]
    },
  ];

  const blogDropdown: DropdownItem[] = [
    { label: "Travel Tips", path: "/blog" },
    { label: "Destination Guides", path: "/blog" },
    { label: "Visa Updates", path: "/blog" },
    { label: "All Blog Posts", path: "/blog" },
  ];

  const navLinks: NavItem[] = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About Us" },
    { to: "/services", label: "Services", dropdown: servicesDropdown },
    { to: "/destinations", label: "Destinations", dropdown: destinationsDropdown },
    { to: "/visa", label: "Visa Guide" },
    { to: "/blog", label: "Blog", dropdown: blogDropdown },
    { to: "/faq", label: "FAQ" },
    { to: "/contact", label: "Contact" },
  ];

  const quickSearchItems = [
    { label: "Kashmir", path: "/destinations/kashmir" },
    { label: "Dubai", path: "/destinations/dubai" },
    { label: "Bali", path: "/destinations/bali" },
    { label: "Maldives", path: "/destinations/maldives" },
    { label: "Visa Services", path: "/visa" },
    { label: "Honeymoon Packages", path: "/services" },
    { label: "Family Tours", path: "/services" },
  ];

  const filteredSearchItems = searchQuery.trim() 
    ? quickSearchItems.filter(item => item.label.toLowerCase().includes(searchQuery.toLowerCase())) 
    : quickSearchItems;

  const handleSearch = (path: string) => {
    setSearchQuery("");
    setShowSearch(false);
    setIsOpen(false);
    navigate(path);
  };

  const handleNavClick = () => {
    setIsOpen(false);
    setMobileExpandedMenu(null);
    setMobileExpandedSubMenu(null);
  };

  // Clear timeouts on unmount
  useEffect(() => {
    return () => {
      if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
      if (submenuTimeoutRef.current) clearTimeout(submenuTimeoutRef.current);
    };
  }, []);

  const handleDropdownEnter = (label: string) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
    setActiveDropdown(label);
  };

  const handleDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
      setActiveSubMenu(null);
    }, 200);
  };

  const handleSubMenuEnter = (label: string) => {
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current);
      submenuTimeoutRef.current = null;
    }
    setActiveSubMenu(label);
  };

  const handleSubMenuLeave = () => {
    submenuTimeoutRef.current = setTimeout(() => {
      setActiveSubMenu(null);
    }, 150);
  };

  const handleMobileMenuToggle = (label: string) => {
    setMobileExpandedMenu(mobileExpandedMenu === label ? null : label);
    setMobileExpandedSubMenu(null);
  };

  const handleMobileSubMenuToggle = (label: string) => {
    setMobileExpandedSubMenu(mobileExpandedSubMenu === label ? null : label);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* ── Top Info Bar ── */}
      <div className={`bg-secondary text-secondary-foreground transition-all duration-300 overflow-hidden ${isScrolled ? "max-h-0 opacity-0" : "max-h-10 opacity-100"}`}>
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-10 flex items-center justify-between h-9 text-[11px] sm:text-xs tracking-wide">
          {/* Left: Address (hidden on mobile) */}
          <div className="hidden md:flex items-center gap-1.5 text-secondary-foreground/80">
            <MapPin className="h-3 w-3 flex-shrink-0" />
            <span>18th Floor, Junomoneta Tower, Pal, Surat</span>
          </div>
          {/* Right: Phone + Email */}
          <div className="flex items-center gap-4 sm:gap-5 ml-auto">
            <a href="tel:+917490019959" className="flex items-center gap-1.5 text-secondary-foreground/90 hover:text-gold-light transition-colors">
              <Phone className="h-3 w-3 flex-shrink-0" />
              <span>+91 74900 19959</span>
            </a>
            <a href="mailto:info@feelfreeholidays.com" className="hidden sm:flex items-center gap-1.5 text-secondary-foreground/90 hover:text-gold-light transition-colors">
              <Mail className="h-3 w-3 flex-shrink-0" />
              <span>info@feelfreeholidays.com</span>
            </a>
          </div>
        </div>
      </div>

      {/* ── Main Navigation Bar ── */}
      <nav
        aria-label="Main navigation"
        className={`bg-card transition-all duration-300 ${isScrolled ? "shadow-[0_2px_12px_rgba(0,0,0,0.06)] border-b border-border/30" : "border-b border-border/40"}`}
      >
        <div className={`max-w-[1400px] mx-auto flex items-center px-4 md:px-6 lg:px-10 transition-all duration-300 ${isScrolled ? "h-[58px] lg:h-[64px]" : "h-[64px] lg:h-[72px]"}`}>
          {/* Left: Logo */}
          <NavLink to="/" className="flex items-center flex-shrink-0 focus-ring" aria-label="Feel Free Holidays Home">
            <img
              alt="Feel Free Holidays logo"
              width="160"
              height="44"
              loading="eager"
              decoding="async"
              fetchPriority="high"
              src={officialLogo}
              className={`w-auto object-contain transition-all duration-300 ${isScrolled ? "h-10 sm:h-11 lg:h-12" : "h-11 sm:h-12 lg:h-[3.25rem]"}`}
            />
          </NavLink>

          {/* Center: Desktop Navigation */}
          <div className="hidden lg:flex items-center justify-center flex-1 px-6">
            <div className="flex items-center gap-0.5">
              {navLinks.map((link) => (
                <div
                  key={link.to}
                  className="relative"
                  onMouseEnter={() => link.dropdown && handleDropdownEnter(link.label)}
                  onMouseLeave={handleDropdownLeave}
                >
                  <NavLink
                    to={link.to}
                    end={link.to === "/"}
                    className="px-3 xl:px-3.5 py-2 text-[13px] font-medium text-foreground/70 hover:text-primary transition-colors duration-200 rounded-md focus-ring inline-flex items-center gap-1 tracking-wide relative after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-[2px] after:bg-primary after:transition-all after:duration-300 hover:after:w-3/4"
                    activeClassName="text-primary font-semibold after:w-3/4"
                  >
                    {link.label}
                    {link.dropdown && (
                      <ChevronDown className={`h-3 w-3 opacity-50 transition-transform duration-200 ${activeDropdown === link.label ? 'rotate-180' : ''}`} />
                    )}
                  </NavLink>

                  {/* Dropdown Menu */}
                  {link.dropdown && activeDropdown === link.label && (
                    <div
                      className="absolute top-full left-0 mt-1 w-56 bg-card border border-border/50 rounded-xl shadow-large py-1.5 z-50 animate-fade-in"
                      onMouseEnter={() => handleDropdownEnter(link.label)}
                      onMouseLeave={handleDropdownLeave}
                    >
                      <NavLink
                        to={link.to}
                        className="block px-4 py-2.5 text-sm font-semibold text-primary hover:bg-primary/5 transition-colors border-b border-border/50 mb-1"
                        onClick={() => setActiveDropdown(null)}
                      >
                        View All {link.label}
                      </NavLink>
                      {link.dropdown.map((item, index) => (
                        <div
                          key={index}
                          className="relative"
                          onMouseEnter={() => item.subItems && handleSubMenuEnter(item.label)}
                          onMouseLeave={() => item.subItems && handleSubMenuLeave()}
                        >
                          {item.subItems ? (
                            <div className="flex items-center justify-between px-4 py-2 text-sm text-foreground/75 hover:text-primary hover:bg-primary/5 transition-colors cursor-pointer rounded-md mx-1">
                              {item.label}
                              <ChevronRight className="h-3.5 w-3.5 opacity-40" />
                            </div>
                          ) : (
                            <NavLink
                              to={item.path}
                              className="block px-4 py-2 text-sm text-foreground/75 hover:text-primary hover:bg-primary/5 transition-colors rounded-md mx-1"
                              onClick={() => setActiveDropdown(null)}
                            >
                              {item.label}
                            </NavLink>
                          )}

                          {/* Nested Sub-menu */}
                          {item.subItems && activeSubMenu === item.label && (
                            <div
                              className="absolute left-full top-0 ml-1 w-48 bg-card border border-border/50 rounded-xl shadow-large py-1.5 z-50 animate-fade-in"
                              onMouseEnter={() => handleSubMenuEnter(item.label)}
                              onMouseLeave={handleSubMenuLeave}
                            >
                              {item.subItems.map((subItem, subIndex) => (
                                <NavLink
                                  key={subIndex}
                                  to={subItem.path}
                                  className="block px-4 py-2 text-sm text-foreground/75 hover:text-primary hover:bg-primary/5 transition-colors rounded-md mx-1"
                                  onClick={() => {
                                    setActiveDropdown(null);
                                    setActiveSubMenu(null);
                                  }}
                                >
                                  {subItem.label}
                                </NavLink>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right: CTA actions + Mobile menu */}
          <div className="flex items-center gap-2.5 flex-shrink-0">
            <a
              href="tel:+917490019959"
              className="hidden lg:inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-foreground/60 hover:text-primary transition-colors rounded-md"
            >
              <Phone className="h-4 w-4" aria-hidden="true" />
              <span>Call Us</span>
            </a>
            <Button size="sm" asChild className="hidden lg:inline-flex h-9 px-5 rounded-lg font-semibold text-sm shadow-sm hover:shadow-md transition-all">
              <NavLink to="/contact">Plan My Trip</NavLink>
            </Button>

            {/* Mobile Menu Button */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden p-2.5 text-foreground rounded-lg hover:bg-muted transition-colors focus-ring" aria-label="Open menu">
                  <Menu className="h-6 w-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:w-[400px] p-0 overflow-y-auto">
                <SheetHeader className="p-5 pb-4 border-b border-border">
                  {/* OFFICIAL LOGO - Do not change without owner approval */}
                  <SheetTitle className="text-left">
                    <img src={officialLogo} alt="Feel Free Holidays logo" className="h-11 w-auto" />
                  </SheetTitle>
                </SheetHeader>

                {/* Search Section */}
                <div className="p-4 border-b border-border">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input 
                      type="text" 
                      placeholder="Search destinations, services..." 
                      value={searchQuery} 
                      onChange={e => {
                        setSearchQuery(e.target.value);
                        setShowSearch(true);
                      }} 
                      onFocus={() => setShowSearch(true)} 
                      className="pl-10 h-11 rounded-xl" 
                    />
                    {searchQuery && (
                      <button 
                        onClick={() => {
                          setSearchQuery("");
                          setShowSearch(false);
                        }} 
                        className="absolute right-3 top-1/2 -translate-y-1/2"
                      >
                        <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                      </button>
                    )}
                  </div>
                  
                  {/* Search Results */}
                  {showSearch && (
                    <div className="mt-3 space-y-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide px-2 mb-2">
                        {searchQuery ? "Search Results" : "Quick Links"}
                      </p>
                      {filteredSearchItems.length > 0 ? (
                        filteredSearchItems.map((item, index) => (
                          <button 
                            key={index} 
                            onClick={() => handleSearch(item.path)} 
                            className="w-full text-left px-3 py-2.5 text-sm text-foreground hover:bg-primary/5 hover:text-primary rounded-lg transition-colors"
                          >
                            {item.label}
                          </button>
                        ))
                      ) : (
                        <p className="px-3 py-2 text-sm text-muted-foreground">No results found</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Navigation Links with Expandable Submenus */}
                <div className="p-4">
                  <nav className="flex flex-col space-y-0.5">
                    {navLinks.map((link) => (
                      <div key={link.to}>
                        {link.dropdown ? (
                          <>
                            <div className="flex items-center rounded-xl hover:bg-primary/5 transition-colors">
                              {/* Left: navigable link to parent page */}
                              <NavLink
                                to={link.to}
                                end={link.to === "/"}
                                className="flex-1 px-4 py-3 text-[15px] font-medium text-foreground hover:text-primary transition-colors"
                                activeClassName="text-primary font-semibold"
                                onClick={handleNavClick}
                              >
                                {link.label}
                              </NavLink>
                              {/* Right: toggle button for submenu */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMobileMenuToggle(link.label);
                                }}
                                className="flex items-center justify-center w-11 h-11 text-muted-foreground hover:text-primary transition-colors flex-shrink-0"
                                aria-label={`Toggle ${link.label} submenu`}
                                aria-expanded={mobileExpandedMenu === link.label}
                              >
                                <ChevronRight className={`h-5 w-5 transition-transform duration-200 ${mobileExpandedMenu === link.label ? 'rotate-90' : ''}`} />
                              </button>
                            </div>
                            {mobileExpandedMenu === link.label && (
                              <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-primary/20 pl-4">
                                {/* View All link */}
                                <NavLink
                                  to={link.to}
                                  className="block px-3 py-2.5 text-sm font-semibold text-primary hover:bg-primary/5 rounded-lg transition-colors border-b border-border/50 mb-1"
                                  onClick={handleNavClick}
                                >
                                  → View All {link.label}
                                </NavLink>
                                {link.dropdown.map((item, index) => (
                                  <div key={index}>
                                    {item.subItems ? (
                                      <>
                                        <button
                                          onClick={() => handleMobileSubMenuToggle(item.label)}
                                          className="w-full px-3 py-2.5 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors flex items-center justify-between"
                                        >
                                          {item.label}
                                          <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${mobileExpandedSubMenu === item.label ? 'rotate-90' : ''}`} />
                                        </button>
                                        {mobileExpandedSubMenu === item.label && (
                                          <div className="ml-3 mt-1 space-y-0.5 border-l-2 border-primary/10 pl-3">
                                            {item.subItems.map((subItem, subIndex) => (
                                              <NavLink
                                                key={subIndex}
                                                to={subItem.path}
                                                className="block px-3 py-2 text-sm text-foreground/70 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                                                onClick={handleNavClick}
                                              >
                                                {subItem.label}
                                              </NavLink>
                                            ))}
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <NavLink
                                        to={item.path}
                                        className="block px-3 py-2.5 text-sm text-foreground/80 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                                        onClick={handleNavClick}
                                      >
                                        {item.label}
                                      </NavLink>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          <NavLink 
                            to={link.to} 
                            end={link.to === "/"} 
                            className="px-4 py-3 text-[15px] font-medium text-foreground hover:text-primary hover:bg-primary/5 rounded-xl transition-colors block" 
                            activeClassName="text-primary bg-primary/10 font-semibold" 
                            onClick={handleNavClick}
                          >
                            {link.label}
                          </NavLink>
                        )}
                      </div>
                    ))}
                  </nav>
                </div>

                {/* CTA Buttons */}
                <div className="p-4 mt-auto border-t border-border space-y-3">
                  <Button variant="outline" size="lg" className="w-full h-12 text-base rounded-xl" asChild>
                    <a href="tel:+917490019959">
                      <Phone className="h-5 w-5 mr-2" />
                      Call Us
                    </a>
                  </Button>
                  <Button size="lg" className="w-full h-12 text-base rounded-xl font-semibold" asChild>
                    <NavLink to="/contact" onClick={handleNavClick}>
                      Plan My Trip
                    </NavLink>
                  </Button>
                  <Button variant="secondary" size="lg" className="w-full h-12 text-base rounded-xl bg-[#25D366] hover:bg-[#22c55e] text-white" asChild>
                    <a 
                      href={`https://wa.me/917490019959?text=${encodeURIComponent("Hello Feel Free Holidays, I'm interested in planning a trip. Please share your best travel packages.")}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <MessageCircle className="h-5 w-5 mr-2" />
                      WhatsApp Chat
                    </a>
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </header>
  );
};
